import React from 'react';

interface LicenseInfoCardProps {
  licenseInfo: {
    type: string;
    url: string | null;
    commercial_use: boolean | null;
    attribution_required: boolean | null;
  };
}

const LicenseInfoCard: React.FC<LicenseInfoCardProps> = ({ licenseInfo }) => {
  const getLicenseBadgeColor = (type: string) => {
    const normalizedType = type.toLowerCase();
    if (normalizedType.includes('mit') || normalizedType.includes('apache') || normalizedType.includes('bsd')) {
      return 'bg-green-50 text-green-700 ring-green-600/20';
    }
    if (normalizedType.includes('gpl') || normalizedType.includes('copyleft')) {
      return 'bg-yellow-50 text-yellow-800 ring-yellow-600/20';
    }
    return 'bg-gray-50 text-gray-700 ring-gray-500/10';
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">License Information</h3>
      <div className="space-y-4">
        <div>
          <span className="text-sm font-medium text-gray-700">License Type</span>
          <div className="mt-1">
            <span className={`inline-flex items-center rounded-md px-2.5 py-0.5 text-sm font-medium ring-1 ring-inset ${getLicenseBadgeColor(licenseInfo.type)}`}>
              {licenseInfo.type || 'Unknown'}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="text-sm font-medium text-gray-700">Commercial Use</span>
            <div className="mt-1 flex items-center gap-2">
              {licenseInfo.commercial_use === true ? (
                <>
                  <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-sm text-green-700">Allowed</span>
                </>
              ) : licenseInfo.commercial_use === false ? (
                <>
                  <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span className="text-sm text-red-700">Not Allowed</span>
                </>
              ) : (
                <span className="text-sm text-gray-500">Unknown</span>
              )}
            </div>
          </div>

          <div>
            <span className="text-sm font-medium text-gray-700">Attribution Required</span>
            <div className="mt-1 flex items-center gap-2">
              {licenseInfo.attribution_required === true ? (
                <>
                  <svg className="h-5 w-5 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span className="text-sm text-yellow-700">Required</span>
                </>
              ) : licenseInfo.attribution_required === false ? (
                <>
                  <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-sm text-green-700">Not Required</span>
                </>
              ) : (
                <span className="text-sm text-gray-500">Unknown</span>
              )}
            </div>
          </div>
        </div>

        {licenseInfo.url && (
          <div>
            <span className="text-sm font-medium text-gray-700">License URL</span>
            <div className="mt-1">
              <a
                href={licenseInfo.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
              >
                {licenseInfo.url}
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LicenseInfoCard;
